import { useState } from 'react';
import { Container, Box, Stepper, Step, StepLabel, Button, Card, Grid, Typography } from '@mui/material';
import Page from '../../../components/Page';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import CustomerSelector from './components/CustomerSelector';
import CakeTypeSelector from './components/CakeTypeSelector';
import CustomCakeBuilder, {
  CustomCakeData,
  getSelectedCakesTotal,
} from './components/CustomCakeBuilder';
import CategoryCakeSelector from './components/CategoryCakeSelector';
import SelectedCakesList from './components/SelectedCakesList';
import SelectedAddonsList from './components/SelectedAddonsList';
import DeliverySelector from './components/DeliverySelector';
import PaymentSelector from './components/PaymentSelector';
import AddonSelector, { SelectedAddon } from './components/AddonSelector';
import { Users } from '../../../store/users/api/interfaces';
import uuidv4 from '../../../utils/uuidv4';

const steps = ['Customer', 'Cakes', 'Delivery', 'Payment'];

interface DeliveryData {
  addressId: string | null;
  deliveryDate: string | null;
  deliveryTimeSlot: string | null;
  isIdentitySecret: boolean;
  recipientName: string | null;
  recipientPhone: string | null;
}

const CreateOrder = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedCustomer, setSelectedCustomer] = useState<Users | null>(null);
  const [cakeMode, setCakeMode] = useState<'custom' | 'category' | 'addons'>('custom');
  const [selectedCakes, setSelectedCakes] = useState<CustomCakeData[]>([]);
  const [editingCakeIndex, setEditingCakeIndex] = useState<number | null>(null);
  const [selectedAddons, setSelectedAddons] = useState<SelectedAddon[]>([]);
  const [deliveryData, setDeliveryData] = useState<DeliveryData | null>(null);

  const handleNext = () => {
    if (activeStep === 0 && !selectedCustomer) {
      return; // Cannot proceed without customer
    }
    if (activeStep === 1 && selectedCakes.length === 0) {
      return; // Cannot proceed without at least one cake
    }
    if (activeStep === 2 && !deliveryData) {
      return; // Cannot proceed without delivery details
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleDeliveryComplete = (delivery: DeliveryData) => {
    setDeliveryData(delivery);
    // Don't auto-advance - let user click Next button
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleCustomerSelect = (customer: Users | null) => {
    setSelectedCustomer(customer);
  };

  const handleCakeComplete = (cake: CustomCakeData) => {
    if (editingCakeIndex !== null) {
      // Update existing cake
      const updatedCakes = [...selectedCakes];
      updatedCakes[editingCakeIndex] = { ...cake, id: updatedCakes[editingCakeIndex].id || uuidv4() };
      setSelectedCakes(updatedCakes);
      setEditingCakeIndex(null);
    } else {
      // Add new cake
      setSelectedCakes([...selectedCakes, { ...cake, id: uuidv4() }]);
    }
  };

  const handleRemoveCake = (index: number) => {
    setSelectedCakes(selectedCakes.filter((_, i) => i !== index));
  };

  const handleEditCake = (index: number, cake: CustomCakeData) => {
    setEditingCakeIndex(index);
    setCakeMode(cake.categoryId ? 'category' : 'custom');
    // TODO: Load cake data into builder for editing
  };

  const handleQuantityChange = (index: number, newQty: number) => {
    const updatedCakes = [...selectedCakes];
    updatedCakes[index] = { ...updatedCakes[index], qty: newQty };
    setSelectedCakes(updatedCakes);
  };

  const handleAddonQuantityChange = (id: string, newQty: number) => {
    if (newQty < 1) {
      handleRemoveAddon(id);
      return;
    }
    const updatedAddons = selectedAddons.map((addon) => {
      if (addon.id === id) {
        return {
          ...addon,
          qty: newQty,
          unitPrice: addon.price * newQty,
        };
      }
      return addon;
    });
    setSelectedAddons(updatedAddons);
  };

  const handleRemoveAddon = (id: string) => {
    setSelectedAddons(selectedAddons.filter((addon) => addon.id !== id));
  };

  return (
    <Page title={'Order: Create'}>
      <Container maxWidth={'xl'}>
        <HeaderBreadcrumbs
          heading={'Create Order'}
          links={[
            { name: 'Dashboard', href: '/' },
            { name: 'Order', href: '/order' },
          ]}
        />

        <Box sx={{ mt: 3, mb: 4 }}>
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Box sx={{ mt: 4 }}>
          {/* Step 1: Customer Selection */}
          {activeStep === 0 && (
            <Card sx={{ p: 3 }}>
              <CustomerSelector
                onCustomerSelect={handleCustomerSelect}
                selectedCustomer={selectedCustomer}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  variant='contained'
                  onClick={handleNext}
                  disabled={!selectedCustomer}
                  sx={{
                    backgroundColor: '#EE3750 !important',
                    '&:hover': {
                      backgroundColor: '#EE3750 !important'
                    },
                    '&:disabled': {
                      backgroundColor: '#c4cdd5',
                      color: '#fff'
                    }
                  }}
                >
                  Next
                </Button>
              </Box>
            </Card>
          )}

          {/* Step 2: Cakes Selection */}
          {activeStep === 1 && (
            <Box>
              <Card sx={{ p: 3, mb: 3 }}>
                <CakeTypeSelector
                  onModeChange={setCakeMode}
                  selectedMode={cakeMode}
                />
              </Card>

              {cakeMode === 'addons' ? (
                <>
                  <Grid container spacing={3}>
                    {/* Left Side: Addon Selector */}
                    <Grid item xs={12} md={selectedAddons.length > 0 ? 7 : 12}>
                      <AddonSelector selectedAddons={selectedAddons} onAddonsChange={setSelectedAddons} />
                    </Grid>

                    {/* Right Side: Selected Addons List */}
                    {selectedAddons.length > 0 && (
                      <Grid item xs={12} md={5}>
                        <SelectedAddonsList
                          addons={selectedAddons}
                          onRemove={handleRemoveAddon}
                          onQuantityChange={handleAddonQuantityChange}
                        />
                      </Grid>
                    )}
                  </Grid>

                  {/* Selected Addons List below (for mobile/small screens) */}
                  {selectedAddons.length > 0 && (
                    <Box sx={{ mt: 3, display: { xs: 'block', md: 'none' } }}>
                      <SelectedAddonsList
                        addons={selectedAddons}
                        onRemove={handleRemoveAddon}
                        onQuantityChange={handleAddonQuantityChange}
                      />
                    </Box>
                  )}
                </>
              ) : (
                <>
              <Grid container spacing={3}>
                {/* Cake Builder - Full width for category, or split for custom */}
                <Grid item xs={12} md={cakeMode === 'category' ? 12 : (selectedCakes.length > 0 ? 7 : 12)}>
                  {cakeMode === 'custom' ? (
                    <CustomCakeBuilder
                      onCakeComplete={handleCakeComplete}
                      editingCake={editingCakeIndex !== null ? selectedCakes[editingCakeIndex] : null}
                    />
                  ) : (
                    <CategoryCakeSelector
                      onCakeComplete={handleCakeComplete}
                      editingCake={editingCakeIndex !== null ? selectedCakes[editingCakeIndex] : null}
                      selectedCakes={selectedCakes}
                      onRemoveCake={handleRemoveCake}
                      onEditCake={handleEditCake}
                      onQuantityChange={handleQuantityChange}
                          selectedAddons={selectedAddons}
                          onRemoveAddon={handleRemoveAddon}
                          onAddonQuantityChange={handleAddonQuantityChange}
                    />
                  )}
                </Grid>

                {/* Right Side: Selected Cakes List (only for custom mode) */}
                    {cakeMode === 'custom' && (selectedCakes.length > 0 || selectedAddons.length > 0) && (
                  <Grid item xs={12} md={5}>
                        {selectedCakes.length > 0 && (
                    <SelectedCakesList
                      cakes={selectedCakes}
                      onRemove={handleRemoveCake}
                      onEdit={handleEditCake}
                      onQuantityChange={handleQuantityChange}
                    />
                        )}
                        {/* Selected Addons below cakes */}
                        {selectedAddons.length > 0 && (
                          <SelectedAddonsList
                            addons={selectedAddons}
                            onRemove={handleRemoveAddon}
                            onQuantityChange={handleAddonQuantityChange}
                          />
                        )}
                  </Grid>
                )}
              </Grid>

              {/* Selected Cakes List below (for mobile/small screens - only for custom mode) */}
                  {cakeMode === 'custom' && (selectedCakes.length > 0 || selectedAddons.length > 0) && (
                <Box sx={{ mt: 3, display: { xs: 'block', md: 'none' } }}>
                      {selectedCakes.length > 0 && (
                  <SelectedCakesList
                    cakes={selectedCakes}
                    onRemove={handleRemoveCake}
                    onEdit={handleEditCake}
                    onQuantityChange={handleQuantityChange}
                  />
                      )}
                      {/* Selected Addons below cakes */}
                      {selectedAddons.length > 0 && (
                        <SelectedAddonsList
                          addons={selectedAddons}
                          onRemove={handleRemoveAddon}
                          onQuantityChange={handleAddonQuantityChange}
                        />
                      )}
                    </Box>
                  )}
                </>
              )}

              {/* Combined Total Summary */}
              {(selectedCakes.length > 0 || selectedAddons.length > 0) && (
                <Card sx={{ p: 3, mt: 3, backgroundColor: '#FDF0F1' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant='h6'>
                      Order Summary
                    </Typography>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant='body2' color='text.secondary' gutterBottom>
                        {selectedCakes.length > 0 && (
                          <>Cakes: {getSelectedCakesTotal(selectedCakes).toFixed(2)} QAR</>
                        )}
                        {selectedCakes.length > 0 && selectedAddons.length > 0 && ' + '}
                        {selectedAddons.length > 0 && (
                          <>Addons: {selectedAddons.reduce((sum, addon) => sum + addon.unitPrice, 0).toFixed(2)} QAR</>
                        )}
                      </Typography>
                      <Typography variant='h5' color='primary' sx={{ fontWeight: 'bold' }}>
                        Grand Total: {(getSelectedCakesTotal(selectedCakes) + selectedAddons.reduce((sum, addon) => sum + addon.unitPrice, 0)).toFixed(2)} QAR
                      </Typography>
                    </Box>
                </Box>
                </Card>
              )}

              {/* Navigation Buttons */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button onClick={handleBack}>Back</Button>
                <Button
                  variant='contained'
                  onClick={handleNext}
                  disabled={selectedCakes.length === 0 && selectedAddons.length === 0}
                  sx={{
                    backgroundColor: '#EE3750 !important',
                    '&:hover': {
                      backgroundColor: '#EE3750 !important'
                    },
                    '&:disabled': {
                      backgroundColor: '#c4cdd5',
                      color: '#fff'
                    }
                  }}
                >
                  Next
                </Button>
              </Box>
            </Box>
          )}

          {/* Step 3: Delivery Details */}
          {activeStep === 2 && (
            <Box>
              <DeliverySelector
                selectedCustomer={selectedCustomer}
                selectedCakes={selectedCakes}
                onDeliveryComplete={handleDeliveryComplete}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button onClick={handleBack}>Back</Button>
                <Button
                  variant='contained'
                  onClick={() => {
                    if (deliveryData) {
                      handleNext();
                    }
                  }}
                  disabled={!deliveryData}
                  sx={{
                    backgroundColor: '#EE3750 !important',
                    '&:hover': {
                      backgroundColor: '#EE3750 !important'
                    },
                    '&:disabled': {
                      backgroundColor: '#c4cdd5',
                      color: '#fff'
                    }
                  }}
                >
                  Next
                </Button>
              </Box>
            </Box>
          )}

          {/* Step 4: Payment & Confirmation */}
          {activeStep === 3 && (
            <Box>
              <PaymentSelector
                selectedCustomer={selectedCustomer}
                selectedCakes={selectedCakes}
                selectedAddons={selectedAddons}
                deliveryData={deliveryData}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 3 }}>
                <Button onClick={handleBack}>Back</Button>
              </Box>
            </Box>
          )}
        </Box>
      </Container>
    </Page>
  );
};

export default CreateOrder;

